<template>
  <div>
    <div class="font-inter w- h-[1040px] bg-[#FFFFFF] rounded-t-[20px] mt-3">
    <NavbarComponent />
    <div class="mx-8 my-6 ">
      <div class="hr-container space-x-5 mb-6 ">
        <div class="flex items-center justify-between">
          <div class="space-y-3">
            <p class="font-semibold text-bodyText text-[20px] space-x-3">Jobs</p>
            <p class="text-bodyText text-[14px]">View and manage all job postings.</p>
          </div>
          <div class="flex gap-4">
            <button @click="openCreateJobModal" class="flex bg-black hover:bg-gray-700 text-white text-[14px] font-medium px-4 py-2 space-x-2 items-center rounded-[8px] h-[42px]">
                <img src="@/assets/dashboard/add.svg" alt="Add icon" class="h-4">
                <span>Create Job</span>
              </button>
          </div>
        </div>
      </div>
    </div>

    <div class="font-inter p-8">
      <div class="mb-4 flex gap-4 items-center">
        <div class="relative w-[320px]">
          <input 
            v-model="searchQuery" 
            @input="handleSearch"
            type="text" 
            placeholder="Search jobs by title or company..." 
            class="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[14px]"
          />
          <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center py-10">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDarkBg"></div>
      </div>

      <div v-else>
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50 text-[12px] font-medium uppercase tracking-wider">
            <tr>
              <th scope="col" class="px-6 py-3 text-left">Title</th>
              <th scope="col" class="px-6 py-3 text-left">Company</th>
              <th scope="col" class="px-6 py-3 text-left">Location</th>
              <th scope="col" class="px-6 py-3 text-left">Type</th>
              <th scope="col" class="px-6 py-3 text-left">Level</th>
              <th scope="col" class="px-6 py-3 text-left">Deadline</th>
              <th scope="col" class="px-6 py-3 text-left">Status</th>
              <th scope="col" class="px-6 py-3 text-left"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="job in jobs" :key="job.id" class="text-[13px] hover:bg-gray-50">
              <td class="px-6 py-4">
                <p class="font-medium text-gray-900">{{ job.title }}</p>
              </td>
              <td class="px-6 py-4 text-gray-700">{{ job.company }}</td>
              <td class="px-6 py-4 text-gray-500">{{ job.location }}</td>
              <td class="px-6 py-4">
                <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-[11px] capitalize">
                  {{ job.job_type.replace('_', ' ') }}
                </span>
              </td>
              <td class="px-6 py-4 capitalize text-gray-500">{{ job.experience_level.replace('_', ' ') }}</td>
              <td class="px-6 py-4 text-gray-500">{{ formatDate(job.application_deadline) }}</td>
              <td class="px-6 py-4">
                <span :class="[
                  'px-2 py-1 rounded-full text-[11px] capitalize',
                  job.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ job.status }}
                </span>
              </td>
              <td class="px-6 py-4 text-right relative">
                <img src="@/assets/projects/options.svg" alt="Options" @click.stop="toggleDropdown(job.id, $event)"
                  class="h-6 cursor-pointer inline-block" />
                
                <div v-if="activeDropdownId === job.id"
                  class="absolute right-0 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 mt-1"
                  @click.stop
                >
                  <button @click="editJob(job)" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 italic flex items-center gap-2">
                    <img src="@/assets/users/edit-2.svg" alt="Edit" class="h-3" />
                    Edit
                  </button>
                  <button @click="openDeleteModal(job)" class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2">
                    <img src="@/assets/users/trash.svg" alt="Delete" class="h-3" />
                    Delete
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="jobs.length === 0">
              <td colspan="8" class="px-6 py-10 text-center text-gray-500 uppercase tracking-widest text-[12px]">
                No jobs found
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="flex justify-between items-center mt-6 text-[13px]">
          <button @click="prevPage" :disabled="currentPage === 1"
            class="flex items-center gap-2 py-2 px-4 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            <img src="@/assets/reports/arrow-left.svg" alt="Prev" class="h-3" />
            Previous
          </button>
          
          <div class="flex gap-2">
            <span v-for="page in totalPages" :key="page" @click="fetchJobs(page)" :class="[
              'px-4 py-2 rounded cursor-pointer border',
              currentPage === page ? 'bg-primaryDarkBg text-white border-primaryDarkBg' : 'border-gray-300 hover:bg-gray-50'
            ]">
              {{ page }}
            </span>
          </div>

          <button @click="nextPage" :disabled="currentPage === totalPages"
            class="flex items-center gap-2 py-2 px-4 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            Next
            <img src="@/assets/reports/arrow-right.svg" alt="Next" class="h-3" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <JobModal 
      :showModal="showJobModal" 
      :selectedJob="selectedJob" 
      @close="showJobModal = false; selectedJob = null" 
      @refresh="fetchJobs(currentPage)" 
    />
    
    <DeleteJobModal 
      :showModal="showDeleteJobModal" 
      :selectedJob="selectedJob" 
      @close="showDeleteJobModal = false; selectedJob = null" 
      @refresh="fetchJobs(currentPage)" 
    />
  </div>
  </div>
</template>

<script>
import apiClient from '@/axios';
import NavbarComponent from '../NavbarComponent.vue';
import JobModal from '../modals/JobModal.vue';
import DeleteJobModal from '../modals/DeleteJobModal.vue';

export default {
  name: 'JobListComponent',
  components: {
    NavbarComponent,
    JobModal,
    DeleteJobModal
  },
  data() {
    return {
      jobs: [],
      loading: true,
      searchQuery: '',
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      activeDropdownId: null,
      showJobModal: false,
      showDeleteJobModal: false,
      selectedJob: null,
      dropdownStyle: {
        top: '0px',
        right: '0px'
      },
      searchDebounceTimer: null
    };
  },
  methods: {
    async fetchJobs(page = 1) {
      this.loading = true;
      this.currentPage = page;
      const skip = (page - 1) * this.pageSize;
      
      try {
        const response = await apiClient.get('/web/jobs/', {
          params: {
            skip: skip,
            limit: this.pageSize,
            q: this.searchQuery || undefined
          }
        });
        
        if (response.data.status_code === 200) {
          this.jobs = response.data.data.jobs;
          this.totalPages = Math.ceil(response.data.data.total / this.pageSize) || 1;
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        this.loading = false;
        this.activeDropdownId = null;
      }
    },
    handleSearch() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchJobs(1);
      }, 500);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    toggleDropdown(id, event) {
      if (this.activeDropdownId === id) {
        this.activeDropdownId = null;
      } else {
        this.activeDropdownId = id;
        const rect = event.target.getBoundingClientRect();
        this.dropdownStyle = {
          top: `${rect.bottom + window.scrollY}px`,
          left: `${rect.left - 160 + window.scrollX}px`
        };
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.fetchJobs(this.currentPage - 1);
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.fetchJobs(this.currentPage + 1);
      }
    },
    openCreateJobModal() {
      this.selectedJob = null;
      this.showJobModal = true;
    },
    editJob(job) {
      this.selectedJob = job;
      this.showJobModal = true;
      this.activeDropdownId = null;
    },
    openDeleteModal(job) {
      this.selectedJob = job;
      this.showDeleteJobModal = true;
      this.activeDropdownId = null;
    }
  },
  mounted() {
    this.fetchJobs();
    document.addEventListener('click', () => {
      this.activeDropdownId = null;
    });
  },
  beforeUnmount() {
    document.removeEventListener('click', () => {
      this.activeDropdownId = null;
    });
  }
};
</script>

<style scoped>
/* Follow existing styling rules */
</style>
